/**
 * AwardOutcomeDialog — capture won/lost outcome after tender close.
 *
 * Closes the learn loop: triggers Award Ghost feedback (genome stats +
 * ghost_weights nudge) on the backend.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Trophy, X, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { setTenderOutcome } from '../../lib/api';
import { markMissionComplete, MISSIONS } from '../../lib/tutorialActions';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

export default function AwardOutcomeDialog({ opp, lang, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState(null);  // 'won' | 'lost'
  const [winnerCompany, setWinnerCompany] = useState('');
  const [winningPrice, setWinningPrice] = useState('');
  const [ourPrice, setOurPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function reset() {
    setOutcome(null);
    setWinnerCompany('');
    setWinningPrice('');
    setOurPrice('');
    setNotes('');
  }

  async function save() {
    if (!outcome) {
      toast.error(t(lang, 'Wybierz wynik', 'Pick outcome'));
      return;
    }
    setSaving(true);
    try {
      await setTenderOutcome(opp.source_id, {
        outcome,
        winner_company: winnerCompany || null,
        winning_price_pln: winningPrice ? parseFloat(winningPrice) : null,
        our_offer_price_pln: ourPrice ? parseFloat(ourPrice) : null,
        notes: notes || null,
      });
      toast.success(t(lang,
        outcome === 'won' ? 'Wygrana zarejestrowana 🏆' : 'Przegrana zarejestrowana — uczymy się',
        outcome === 'won' ? 'Win recorded 🏆' : 'Loss recorded — learning'));
      markMissionComplete(MISSIONS.RECORD_OUTCOME);
      setOpen(false);
      reset();
      if (onDone) onDone(outcome);
    } catch (err) {
      toast.error(err.response?.data?.detail || t(lang, 'Błąd zapisu', 'Save error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" data-testid="award-outcome-trigger">
            <Trophy className="w-3.5 h-3.5 mr-1" />
            {t(lang, 'Wpisz wynik', 'Record outcome')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t(lang, 'Wynik przetargu', 'Tender outcome')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Outcome buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setOutcome('won')}
              data-testid="outcome-won"
              className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-1 ${
                outcome === 'won'
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                  : 'border-border hover:border-emerald-500/50 hover:bg-emerald-500/5'
              }`}
            >
              <Trophy className="w-5 h-5" />
              <span className="text-xs font-medium">{t(lang, 'Wygrana', 'Won')}</span>
            </button>
            <button
              type="button"
              onClick={() => setOutcome('lost')}
              data-testid="outcome-lost"
              className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-1 ${
                outcome === 'lost'
                  ? 'border-red-500 bg-red-500/10 text-red-300'
                  : 'border-border hover:border-red-500/50 hover:bg-red-500/5'
              }`}
            >
              <X className="w-5 h-5" />
              <span className="text-xs font-medium">{t(lang, 'Przegrana', 'Lost')}</span>
            </button>
            <button
              type="button"
              onClick={() => setOutcome('unknown')}
              data-testid="outcome-unknown"
              className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-1 ${
                outcome === 'unknown'
                  ? 'border-amber-500 bg-amber-500/10 text-amber-300'
                  : 'border-border hover:border-amber-500/50 hover:bg-amber-500/5'
              }`}
            >
              <HelpCircle className="w-5 h-5" />
              <span className="text-xs font-medium">{t(lang, 'Nie wiadomo', 'Unknown')}</span>
            </button>
          </div>

          {outcome === 'lost' && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div>
                <Label className="text-xs">{t(lang, 'Kto wygrał', 'Winner company')}</Label>
                <Input
                  value={winnerCompany} onChange={(e) => setWinnerCompany(e.target.value)}
                  placeholder={t(lang, 'np. ZPAS Sp. z o.o.', 'e.g. ZPAS Ltd.')}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t(lang, 'Cena zwycięzcy (PLN)', 'Winning price (PLN)')}</Label>
                  <Input
                    type="number" step="0.01"
                    value={winningPrice} onChange={(e) => setWinningPrice(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t(lang, 'Nasza oferta (PLN)', 'Our offer (PLN)')}</Label>
                  <Input
                    type="number" step="0.01"
                    value={ourPrice} onChange={(e) => setOurPrice(e.target.value)}
                  />
                </div>
              </div>
              {winningPrice && ourPrice && (
                <p className="text-xs text-muted-foreground">
                  {parseFloat(ourPrice) > parseFloat(winningPrice) ? (
                    <span className="text-amber-400">
                      ⚠ {t(lang, 'Byliśmy droższy o', 'We were higher by')}{' '}
                      {(((parseFloat(ourPrice) - parseFloat(winningPrice)) / parseFloat(winningPrice)) * 100).toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-emerald-400">
                      ✓ {t(lang, 'Byliśmy tańszy o', 'We were lower by')}{' '}
                      {(((parseFloat(winningPrice) - parseFloat(ourPrice)) / parseFloat(winningPrice)) * 100).toFixed(1)}%
                    </span>
                  )}
                </p>
              )}
            </div>
          )}

          {outcome === 'won' && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div>
                <Label className="text-xs">{t(lang, 'Nasza cena (PLN)', 'Our price (PLN)')}</Label>
                <Input
                  type="number" step="0.01"
                  value={ourPrice} onChange={(e) => setOurPrice(e.target.value)}
                />
              </div>
            </div>
          )}

          {outcome && (
            <div>
              <Label className="text-xs">{t(lang, 'Notatki', 'Notes')}</Label>
              <Textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder={t(lang,
                  'Co poszło dobrze / źle? Co zrobić inaczej następnym razem?',
                  'What went right/wrong? What to do differently?')}
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t(lang, 'Anuluj', 'Cancel')}
          </Button>
          <Button onClick={save} disabled={!outcome || saving} data-testid="outcome-save">
            {saving ? t(lang, 'Zapisuję...', 'Saving...') : t(lang, 'Zapisz wynik', 'Save outcome')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
